import React from 'react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { LogIn, User, ArrowRight, Smartphone, Lock, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface GuestCheckoutChoiceProps {
  payeeName?: string;
  payeeType?: 'merchant' | 'user';
  onContinueAsGuest: () => void;
  loading?: boolean;
}

/**
 * GuestCheckoutChoice - Step 1 of Guest Checkout PRD
 * 
 * Implements the authentication decision screen where users choose between:
 * 1. Sign In - Log in with existing account
 * 2. Continue as Guest - Proceed with guest checkout
 */
const GuestCheckoutChoice: React.FC<GuestCheckoutChoiceProps> = ({
  payeeName,
  payeeType = 'merchant',
  onContinueAsGuest,
  loading = false
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen w-full max-w-full min-w-0 items-center justify-center overflow-x-hidden bg-gradient-to-br from-blue-50 to-indigo-100 p-4 dark:from-gray-900 dark:to-gray-800">
      <div className="w-full max-w-2xl min-w-0">
        {/* Main Content - Two Column Choice */}
        <div className="mb-6 grid gap-6 md:grid-cols-2">
          {/* Option 1: Sign In */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-400">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <LogIn className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle>Sign In</CardTitle>
              </div>
              <CardDescription>
                Use your existing account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  Access your payment history
                </li>
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  Use saved payment methods
                </li>
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  Manage your account balance
                </li>
              </ul>
              <Button
                onClick={() => navigate('/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search))}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Sign In
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Option 2: Continue as Guest */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-green-200 hover:border-green-400 bg-green-50/50 dark:bg-green-950/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle className="text-green-900 dark:text-green-100">
                  Continue as Guest
                </CardTitle>
              </div>
              <CardDescription className="text-green-800 dark:text-green-300">
                Quick payment, no account needed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Pay in less than 30 seconds
                </li>
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  No account creation needed
                </li>
                <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Get confirmation code instantly
                </li>
              </ul>
              <Button
                onClick={onContinueAsGuest}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? 'Setting up...' : 'Continue as Guest'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Security Info */}
        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
          <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-300">
            Your payment information is encrypted and secure. Both sign-in and guest checkout are protected by bank-level encryption.
          </AlertDescription>
        </Alert>

        {/* Guest Benefits - Optional footer */}
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            <Zap className="w-4 h-4 inline mr-2 text-yellow-500" />
            Guest Checkout Benefits
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex gap-3">
              <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Mobile Friendly</p>
                <p className="text-gray-600 dark:text-gray-400">Optimized for phones</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Zap className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Instant Payment</p>
                <p className="text-gray-600 dark:text-gray-400">Confirm in seconds</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Secure</p>
                <p className="text-gray-600 dark:text-gray-400">Bank-level encryption</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestCheckoutChoice;
